import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  FaUsers, 
  FaBook, 
  FaCalendarAlt, 
  FaMale, 
  FaFemale, 
  FaChartLine,
  FaUserCheck,
  FaUserTimes,
  FaBookReader,
  FaChartPie,
  FaStar,
  FaList,
  FaPrint,
  FaUserFriends,
  FaBookOpen,
  FaCalendarCheck
} from 'react-icons/fa';
import { 
  LineChart, 
  AreaChart,
  Area,
  BarChart, 
  Bar, 
  PieChart, 
  Pie, 
  Cell,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import {
  getLibraryMembers,
  getLibraryBooks,
  getAttendanceRecords,
  getLibraryStatistics,
  getLibraryCategories,
  LibraryMember,
  LibraryBook,
  AttendanceRecord,
  LibraryCategory
} from '../../../../firebase/libraryFirestoreService';
import { useDashboardFilters } from '../../../../contexts/DashboardFilterContext';

// Cache keys and expiry
const DASHBOARD_CACHE_VERSION = 'v2';
const DASHBOARD_CACHE_KEY = `library_dashboard_cache_${DASHBOARD_CACHE_VERSION}`;
const LIBRARY_MEMBERS_CACHE_KEY = `library_members_cache_${DASHBOARD_CACHE_VERSION}`;
const LIBRARY_MEMBERS_DASHBOARD_LIMIT = 2000;
const DASHBOARD_CACHE_EXPIRY = 30 * 60 * 1000; // 30 minutes

// Helper function to calculate age from DOB
const calculateAge = (dob: string): number => {
  if (!dob) return 0;
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

// Categorize age groups
const categorizeAge = (age: number): string => {
  if (age <= 12) return 'Children';
  if (age <= 17) return 'Teenagers';
  if (age <= 24) return 'Young Adults';
  return 'Senior';
};

// Normalize Firestore Timestamp | string | number to JS Date
const normalizeDate = (timestamp: any): Date => {
  if (!timestamp) return new Date();
  if (timestamp.toDate && typeof timestamp.toDate === 'function') return timestamp.toDate();
  return new Date(timestamp);
};

const LibraryDashboard: React.FC = () => {
  const [monthlyData, setMonthlyData] = useState<any[]>([]);
  const [ageDistributionData, setAgeDistributionData] = useState<any[]>([
    { name: 'Children', value: 0, color: '#FF6384' },
    { name: 'Teenagers', value: 0, color: '#36A2EB' },
    { name: 'Young Adults', value: 0, color: '#FFCE56' },
    { name: 'Senior', value: 0, color: '#4BC0C0' }
  ]);
  const [activeSlice, setActiveSlice] = useState<any | null>(null);
  const [totalMembersCount, setTotalMembersCount] = useState<number>(0);
  const [topBooksData, setTopBooksData] = useState<any[]>([]);
  const [topAttendeesData, setTopAttendeesData] = useState<any[]>([]);
  const [booksByCategoryData, setBooksByCategoryData] = useState<any[]>([]);
  const [categoryActivityData, setCategoryActivityData] = useState<any[]>([]);
  const [dailyData, setDailyData] = useState<any[]>([]);
  const [stats, setStats] = useState({
    totalMembers: 0,
    maleMembers: 0,
    femaleMembers: 0,
    totalBooks: 0,
    availableBooks: 0,
    totalAttendance: 0,
    todayAttendance: 0,
    activeMembers: 0,
    semiActiveMembers: 0,
    inactiveMembers: 0,
    activeMale: 0,
    activeFemale: 0,
    semiActiveMale: 0,
    semiActiveFemale: 0,
    inactiveMale: 0,
    inactiveFemale: 0
  });
  const [prevStats, setPrevStats] = useState({
    totalMembers: 0,
    maleMembers: 0,
    femaleMembers: 0,
    totalBooks: 0,
    availableBooks: 0,
    totalAttendance: 0,
    todayAttendance: 0,
    activeMembers: 0,
    semiActiveMembers: 0,
    inactiveMembers: 0,
    activeMale: 0,
    activeFemale: 0,
    semiActiveMale: 0,
    semiActiveFemale: 0,
    inactiveMale: 0,
    inactiveFemale: 0
  });

  // Data sources (we'll use cached versions if available)
  const [members, setMembers] = useState<LibraryMember[]>([]);
  const [books, setBooks] = useState<LibraryBook[]>([]);
  const [attendance, setAttendance] = useState<AttendanceRecord[]>([]);
  const [categories, setCategories] = useState<LibraryCategory[]>([]);
  const [loading, setLoading] = useState(true);

  const { month, monthFrom, monthTo, year } = useDashboardFilters();
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  // Cache functions
  const isCacheValid = (cacheKey: string, expiry: number): boolean => {
    const cacheData = localStorage.getItem(cacheKey);
    if (!cacheData) return false;
    
    try {
      const { timestamp, data, filterKey } = JSON.parse(cacheData);
      const currentFilterKey = `${year ?? 'all'}_${month ?? 'all'}_${monthFrom ?? 'all'}_${monthTo ?? 'all'}_${dateFrom || 'all'}_${dateTo || 'all'}`;
      
      // Check if cache is for current filters and not expired
      return filterKey === currentFilterKey && Date.now() - timestamp < expiry;
    } catch {
      return false;
    }
  };

  const getCachedData = <T,>(cacheKey: string): T | null => {
    const cacheData = localStorage.getItem(cacheKey);
    if (!cacheData) return null;
    
    try {
      const { data } = JSON.parse(cacheData);
      return data;
    } catch {
      return null;
    }
  };

  const saveToCache = <T,>(cacheKey: string, data: T, filterKey: string) => {
    const cacheData = {
      timestamp: Date.now(),
      data,
      filterKey
    };
    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
  };

  const invalidateCache = (cacheKey: string) => {
    localStorage.removeItem(cacheKey);
  };

  // Build filter key for cache
  const getFilterKey = () => {
    return `${year ?? 'all'}_${month ?? 'all'}_${monthFrom ?? 'all'}_${monthTo ?? 'all'}_${dateFrom || 'all'}_${dateTo || 'all'}`;
  };

  // Try to load individual data from cache
  const loadFromIndividualCaches = async () => {
    const membersCache = getCachedData<LibraryMember[]>(LIBRARY_MEMBERS_CACHE_KEY);
    const booksCache = getCachedData<LibraryBook[]>('library_books_cache');
    const attendanceCache = getCachedData<AttendanceRecord[]>('attendance_records_cache');
    const categoriesCache = getCachedData<LibraryCategory[]>('library_categories_cache');

    if (membersCache) setMembers(membersCache);
    if (booksCache) setBooks(booksCache);
    if (attendanceCache) setAttendance(attendanceCache);
    if (categoriesCache) setCategories(categoriesCache);

    return {
      members: membersCache || [],
      books: booksCache || [],
      attendance: attendanceCache || [],
      categories: categoriesCache || []
    };
  };

  // Process data locally (no Firebase calls)
  // Returns a computed snapshot object instead of directly setting React state.
  const processDashboardData = async (cachedData: {
    members: LibraryMember[];
    books: LibraryBook[];
    attendance: AttendanceRecord[];
    categories: LibraryCategory[];
  }) => {
    const { members, books, attendance, categories } = cachedData;

    // Apply date filters to attendance
    const filteredAttendance = attendance.filter((record: any) => {
      const d = normalizeDate(record.timeRecorded);

      // If a full date range is provided, use it (inclusive)
      if (dateFrom || dateTo) {
        try {
          const from = dateFrom ? new Date(dateFrom) : new Date('1970-01-01');
          const to = dateTo ? new Date(dateTo) : new Date('2999-12-31');
          from.setHours(0,0,0,0);
          to.setHours(23,59,59,999);
          return d >= from && d <= to;
        } catch (e) {
          // fall back to month/year filters if parse fails
        }
      }

      if (year != null && d.getFullYear() !== year) return false;
      const recMonth = d.getMonth() + 1;

      // If both from/to provided, treat as inclusive range
      if (monthFrom != null && monthTo != null) {
        const from = Math.min(monthFrom, monthTo);
        const to = Math.max(monthFrom, monthTo);
        if (recMonth < from || recMonth > to) return false;
      } else if (monthFrom != null) {
        if (recMonth !== monthFrom) return false;
      } else if (monthTo != null) {
        if (recMonth !== monthTo) return false;
      } else if (month != null) {
        if (recMonth !== month) return false;
      }

      return true;
    });

    // Calculate stats from members.
    // If a date range is set, derive stats only for members who appear in the filtered attendance
    const memberIdsInRange = new Set(filteredAttendance.map((r: any) => r.memberId).filter(Boolean));
    const membersForStats = (dateFrom || dateTo)
      ? members.filter(m => {
          const key = (m as any).id ?? (m as any).memberId ?? (m as any).uid;
          return key ? memberIdsInRange.has(key) : false;
        })
      : members;

    const totalMembers = membersForStats.length;
    const maleMembers = membersForStats.filter(m => (m.gender || '').toLowerCase() === 'male').length;
    const femaleMembers = membersForStats.filter(m => (m.gender || '').toLowerCase() === 'female').length;
    const activeMembers = membersForStats.filter(m => String(m.status || '').toLowerCase() === 'active').length;
    const semiActiveMembers = membersForStats.filter(m => String(m.status || '').toLowerCase().includes('semi')).length;
    const inactiveMembers = membersForStats.filter(m => {
      const s = String(m.status || '').trim().toLowerCase();
      return s === 'inactive' || s === '' || s === 'null' || s === 'undefined';
    }).length;

    // Calculate gender split per status (based on membersForStats)
    const activeMale = membersForStats.filter(m =>
      String(m.status || '').toLowerCase() === 'active' &&
      String(m.gender || '').toLowerCase() === 'male'
    ).length;

    const activeFemale = membersForStats.filter(m =>
      String(m.status || '').toLowerCase() === 'active' &&
      String(m.gender || '').toLowerCase() === 'female'
    ).length;

    const semiActiveMale = membersForStats.filter(m =>
      String(m.status || '').toLowerCase().includes('semi') &&
      String(m.gender || '').toLowerCase() === 'male'
    ).length;

    const semiActiveFemale = membersForStats.filter(m =>
      String(m.status || '').toLowerCase().includes('semi') &&
      String(m.gender || '').toLowerCase() === 'female'
    ).length;

    const inactiveMale = membersForStats.filter(m => {
      const s = String(m.status || '').trim().toLowerCase();
      return (s === 'inactive' || s === '' || s === 'null' || s === 'undefined') &&
             String(m.gender || '').toLowerCase() === 'male';
    }).length;

    const inactiveFemale = membersForStats.filter(m => {
      const s = String(m.status || '').trim().toLowerCase();
      return (s === 'inactive' || s === '' || s === 'null' || s === 'undefined') &&
             String(m.gender || '').toLowerCase() === 'female';
    }).length;

    // Calculate books stats
    const totalBooks = books.reduce((sum, book) => sum + (book.totalCopies || 0), 0);
    const availableBooks = books.reduce((sum, book) => sum + (book.availableCopies || 0), 0);

    // (moved save-to-cache effect to component scope to avoid hook rules violation)

    // Calculate today's attendance
const todayStr = new Date().toISOString().split('T')[0];
const todayAttendance = filteredAttendance.filter(record => {
  const recordDate = normalizeDate(record.timeRecorded);
  return recordDate.toISOString().split('T')[0] === todayStr;
}).length;

    const statsSnapshot = {
      totalMembers,
      maleMembers,
      femaleMembers,
      totalBooks,
      availableBooks,
      totalAttendance: filteredAttendance.length,
      todayAttendance, // Add this
      activeMembers,
      semiActiveMembers,
      inactiveMembers,
      activeMale,
      activeFemale,
      semiActiveMale,
      semiActiveFemale,
      inactiveMale,
      inactiveFemale
    };

    // Calculate age distribution (based on membersForStats when date filter active)
    const ageGroups: Record<string, number> = {};
    membersForStats.forEach(member => {
      const age = calculateAge(member.dob);
      const ageGroup = categorizeAge(age);
      ageGroups[ageGroup] = (ageGroups[ageGroup] || 0) + 1;
    });

    const ageDistribution = [
      { name: 'Children', value: ageGroups['Children'] || 0, color: '#FF6384' },
      { name: 'Teenagers', value: ageGroups['Teenagers'] || 0, color: '#36A2EB' },
      { name: 'Young Adults', value: ageGroups['Young Adults'] || 0, color: '#FFCE56' },
      { name: 'Senior', value: ageGroups['Senior'] || 0, color: '#4BC0C0' }
    ];

    // Calculate monthly data for charts
    const monthlyMap: Record<string, { attendance: number; uniqueMembers: Set<string> }> = {};

    filteredAttendance.forEach(record => {
      const date = normalizeDate(record.timeRecorded);
      const monthYear = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;

      if (!monthlyMap[monthYear]) {
        monthlyMap[monthYear] = {
          attendance: 0,
          uniqueMembers: new Set()
        };
      }

      monthlyMap[monthYear].attendance++;
      monthlyMap[monthYear].uniqueMembers.add(record.memberId);
    });

    // Convert to array for chart, get last 12 months
    const months = Object.keys(monthlyMap).sort().slice(-12);
    const monthlyChartData = months.map(monthKey => {
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const [yearStr, monthStr] = monthKey.split('-');
      const monthName = monthNames[parseInt(monthStr) - 1];

      return {
        month: `${monthName} ${yearStr.slice(-2)}`,
        attendance: monthlyMap[monthKey].attendance,
        uniqueMembers: monthlyMap[monthKey].uniqueMembers.size
      };
    });

    // Top 10 attendees
    const attendeeCounts: Record<string, { count: number; name: string; memberId: string }> = {};

    filteredAttendance.forEach(record => {
      const key = record.memberId;
      if (!attendeeCounts[key]) {
        attendeeCounts[key] = {
          count: 0,
          name: record.fullName,
          memberId: record.memberId
        };
      }
      attendeeCounts[key].count++;
    });

    const topAttendees = Object.values(attendeeCounts)
      .sort((a, b) => b.count - a.count)
      .slice(0, 10)
      .map((attendee, index) => ({
        id: index + 1,
        name: attendee.name,
        visits: attendee.count,
        memberId: attendee.memberId
      }));

    // Top Read Books (exclude 'Self Study')
    const readCounts: Record<string, number> = {};
    filteredAttendance.forEach((rec: any) => {
      const title = (rec.bookTitle || '').trim();
      if (!title) return;
      const low = title.toLowerCase();
      if (low === 'self study' || low.includes('self study')) return;
      readCounts[title] = (readCounts[title] || 0) + 1;
    });

    const topBooksFromReads = Object.entries(readCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10)
      .map(([title, count], idx) => {
        const book = books.find(b => (b.title || '').trim().toLowerCase() === title.trim().toLowerCase());
        return {
          id: book?.id || `unknown-${idx}`,
          title,
          author: book?.author || 'Unknown',
          reads: count
        };
      });

    // Books by category
    const categoryMap: Record<string, number> = {};
    books.forEach(book => {
      if (book.categoryId) {
        categoryMap[book.categoryId] = (categoryMap[book.categoryId] || 0) + 1;
      }
    });

    const booksByCategory = Object.entries(categoryMap).map(([categoryId, count]) => {
      const category = categories.find(c => c.id === categoryId);
      return {
        category: category?.name || 'Unknown',
        count,
        color: category?.color || '#3b82f6'
      };
    }).sort((a, b) => b.count - a.count).slice(0, 5);

    // Category activity
    const categoryReadsMap: Record<string, number> = {};
    const categoryColorMap: Record<string, string> = {};
    categories.forEach((c: any) => {
      if (c && c.name) categoryColorMap[c.name] = c.color || '#3b82f6';
    });

    filteredAttendance.forEach((rec: any) => {
      const title = (rec.bookTitle || '').trim();
      if (!title) return;
      const low = title.toLowerCase();
      if (low === 'self study' || low.includes('self study')) return;

      const bookMatch = books.find((b: any) => (b.title || '').trim().toLowerCase() === title.trim().toLowerCase());
      const categoryName = bookMatch ? (categories.find((c: any) => c.id === bookMatch.categoryId)?.name) : undefined;
      const key = categoryName || 'Unknown';
      categoryReadsMap[key] = (categoryReadsMap[key] || 0) + 1;
    });

    const categoryActivityArr = Object.entries(categoryReadsMap).map(([name, count]) => ({
      category: name,
      reads: count,
      color: categoryColorMap[name] || '#3b82f6'
    })).sort((a, b) => b.reads - a.reads).slice(0, 12);

    // Daily data: support single-month view and full date-range view when dateFrom/dateTo provided
    let daily: any[] = [];
    try {
      if (dateFrom || dateTo) {
        const from = dateFrom ? new Date(dateFrom) : new Date('1970-01-01');
        const to = dateTo ? new Date(dateTo) : new Date();
        from.setHours(0, 0, 0, 0);
        to.setHours(23, 59, 59, 999);

        const dayMap: Record<string, { attendance: number; unique: Set<string> }> = {};
        for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
          const key = d.toISOString().split('T')[0];
          dayMap[key] = { attendance: 0, unique: new Set() };
        }

        filteredAttendance.forEach((rec: any) => {
          const dt = normalizeDate(rec.timeRecorded);
          const key = dt.toISOString().split('T')[0];
          if (dayMap[key]) {
            dayMap[key].attendance++;
            if (rec.memberId) dayMap[key].unique.add(rec.memberId);
          }
        });

        const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        daily = Object.keys(dayMap).sort().map(key => {
          const d = new Date(key);
          const att = dayMap[key].attendance || 0;
          const uniq = dayMap[key].unique.size || 0;
          const repeat = Math.max(0, att - uniq);
          const label = `${monthNames[d.getMonth()]} ${String(d.getDate()).padStart(2, '0')}`;
          return { day: label, date: key, unique: uniq, repeat, attendance: att };
        });
      } else {
        const selectedMonth = month ?? (new Date().getMonth() + 1);
        const selectedYear = year ?? new Date().getFullYear();

        const dayMap: Record<number, { attendance: number; unique: Set<string> }> = {};
        for (let d = 1; d <= 31; d++) dayMap[d] = { attendance: 0, unique: new Set() };

        filteredAttendance.forEach((rec: any) => {
          const dt = normalizeDate(rec.timeRecorded);
          const day = dt.getDate();
          if ((dt.getMonth() + 1) === selectedMonth && dt.getFullYear() === selectedYear) {
            dayMap[day].attendance++;
            if (rec.memberId) dayMap[day].unique.add(rec.memberId);
          }
        });

        daily = [];
        for (let d = 1; d <= 31; d++) {
          const att = dayMap[d].attendance || 0;
          const uniq = dayMap[d].unique.size || 0;
          const repeat = Math.max(0, att - uniq);
          daily.push({ day: String(d), unique: uniq, repeat, attendance: att });
        }
      }
    } catch (e) {
      console.warn('Error computing daily data', e);
      daily = [];
    }

    return {
      monthlyData: monthlyChartData,
      ageDistributionData: ageDistribution,
      totalMembersCount: totalMembers,
      topBooksData: topBooksFromReads,
      topAttendeesData: topAttendees,
      booksByCategoryData: booksByCategory,
      categoryActivityData: categoryActivityArr,
      dailyData: daily,
        prevStats: stats,
      stats: statsSnapshot,
      members: cachedData.members,
      books: cachedData.books,
      attendance: cachedData.attendance,
      categories: cachedData.categories
    };
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    
    try {
      // Check if we have valid dashboard cache
      const filterKey = getFilterKey();
      const cacheKey = `${DASHBOARD_CACHE_KEY}_${filterKey}`;
      
      if (isCacheValid(cacheKey, DASHBOARD_CACHE_EXPIRY)) {
        const cachedDashboard = getCachedData<any>(cacheKey);
        if (cachedDashboard) {
          console.log('📊 Using cached dashboard data');
          
          // Restore all state from cache
          setMonthlyData(cachedDashboard.monthlyData || []);
          setAgeDistributionData(cachedDashboard.ageDistributionData || []);
          setTotalMembersCount(cachedDashboard.totalMembersCount || 0);
          setTopBooksData(cachedDashboard.topBooksData || []);
          setTopAttendeesData(cachedDashboard.topAttendeesData || []);
          setBooksByCategoryData(cachedDashboard.booksByCategoryData || []);
          setCategoryActivityData(cachedDashboard.categoryActivityData || []);
          setDailyData(cachedDashboard.dailyData || []);
          setPrevStats(cachedDashboard.prevStats || prevStats);
          setStats(cachedDashboard.stats || stats);
          setMembers(cachedDashboard.members || []);
          setBooks(cachedDashboard.books || []);
          setAttendance(cachedDashboard.attendance || []);
          setCategories(cachedDashboard.categories || []);
          
          setLoading(false);
          return;
        }
      }

      console.log('🔄 Fetching fresh dashboard data from caches');

      // Try to load data from individual caches first
      const cachedData = await loadFromIndividualCaches();
      
      // If any data is missing from cache, we need to fetch fresh
      const needsFreshData = 
        cachedData.members.length === 0 ||
        cachedData.books.length === 0 ||
        cachedData.attendance.length === 0 ||
        cachedData.categories.length === 0;

      if (needsFreshData) {
        console.log('⚠️ Some data missing from cache, fetching from Firebase');
        
        // Fetch missing data from Firebase
        const fetchPromises = [];
        
        if (cachedData.members.length === 0) {
          fetchPromises.push(
            getLibraryMembers({ limitCount: LIBRARY_MEMBERS_DASHBOARD_LIMIT }).then(result => {
              if (result.success) {
                cachedData.members = result.data;
                setMembers(result.data);
                saveToCache(LIBRARY_MEMBERS_CACHE_KEY, result.data, getFilterKey());
              }
            })
          );
        }
        
        if (cachedData.books.length === 0) {
          fetchPromises.push(
            getLibraryBooks().then(result => {
              if (result.success) {
                cachedData.books = result.data;
                setBooks(result.data);
              }
            })
          );
        }
        
        if (cachedData.attendance.length === 0) {
          fetchPromises.push(
            getAttendanceRecords().then(result => {
              if (result.success) {
                cachedData.attendance = result.data;
                setAttendance(result.data);
              }
            })
          );
        }
        
        if (cachedData.categories.length === 0) {
          fetchPromises.push(
            getLibraryCategories().then(result => {
              if (result.success) {
                cachedData.categories = result.data;
                setCategories(result.data);
              }
            })
          );
        }
        
        await Promise.all(fetchPromises);
      }

      const statisticsResult = await getLibraryStatistics();
      const libraryStats = statisticsResult.success ? statisticsResult.data : null;

      // Process the data (whether from cache or fresh)
      // Process the data (whether from cache or fresh)
const processedData = await processDashboardData(cachedData);
const finalStats = {
  ...processedData.stats,
  totalMembers: !dateFrom && !dateTo && libraryStats?.totalMembers
    ? Math.max(libraryStats.totalMembers, processedData.stats.totalMembers)
    : processedData.stats.totalMembers,
  activeMembers: !dateFrom && !dateTo && libraryStats?.activeMembers
    ? Math.max(libraryStats.activeMembers, processedData.stats.activeMembers)
    : processedData.stats.activeMembers,
  totalBooks: libraryStats?.totalBooks || processedData.stats.totalBooks,
  availableBooks: libraryStats?.availableBooks || processedData.stats.availableBooks,
  todayAttendance: libraryStats?.todayAttendance ?? processedData.stats.todayAttendance
};
const finalTotalMembersCount = finalStats.totalMembers;

// Update all state variables with the processed data
setMonthlyData(processedData.monthlyData);
setAgeDistributionData(processedData.ageDistributionData);
setTotalMembersCount(finalTotalMembersCount);
setTopBooksData(processedData.topBooksData);
setTopAttendeesData(processedData.topAttendeesData);
setBooksByCategoryData(processedData.booksByCategoryData);
setCategoryActivityData(processedData.categoryActivityData);
setDailyData(processedData.dailyData);
setPrevStats(processedData.prevStats);
setStats(finalStats);
setMembers(processedData.members);
setBooks(processedData.books);
setAttendance(processedData.attendance);
setCategories(processedData.categories);

// Save processed dashboard to cache
const dashboardCacheData = {
  monthlyData: processedData.monthlyData,
  ageDistributionData: processedData.ageDistributionData,
  totalMembersCount: finalTotalMembersCount,
  topBooksData: processedData.topBooksData,
  topAttendeesData: processedData.topAttendeesData,
  booksByCategoryData: processedData.booksByCategoryData,
  categoryActivityData: processedData.categoryActivityData,
  dailyData: processedData.dailyData,
  prevStats: processedData.prevStats,
  stats: finalStats,
  members: processedData.members,
  books: processedData.books,
  attendance: processedData.attendance,
  categories: processedData.categories
};

saveToCache(cacheKey, dashboardCacheData, filterKey);
      console.log('💾 Saved dashboard data to cache');

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      
      // Try to use stale cache as fallback
      const filterKey = getFilterKey();
      const cacheKey = `${DASHBOARD_CACHE_KEY}_${filterKey}`;
      const staleCache = getCachedData<any>(cacheKey);
      
      if (staleCache) {
        console.log('⚠️ Using stale cached dashboard data as fallback');
        setMonthlyData(staleCache.monthlyData || []);
        setAgeDistributionData(staleCache.ageDistributionData || []);
        setTotalMembersCount(staleCache.totalMembersCount || 0);
        setTopBooksData(staleCache.topBooksData || []);
        setTopAttendeesData(staleCache.topAttendeesData || []);
        setBooksByCategoryData(staleCache.booksByCategoryData || []);
        setCategoryActivityData(staleCache.categoryActivityData || []);
        setDailyData(staleCache.dailyData || []);
        setPrevStats(staleCache.prevStats || prevStats);
        setStats(staleCache.stats || stats);
        setMembers(staleCache.members || []);
        setBooks(staleCache.books || []);
        setAttendance(staleCache.attendance || []);
        setCategories(staleCache.categories || []);
      }
    } finally {
      setLoading(false);
    }
  };

  // Cache status indicator
  const cacheStatus = useMemo(() => {
    if (loading) return 'Loading...';
    const filterKey = getFilterKey();
    const cacheKey = `${DASHBOARD_CACHE_KEY}_${filterKey}`;
    if (isCacheValid(cacheKey, DASHBOARD_CACHE_EXPIRY)) return '📊 Using cached dashboard';
    return '🔄 Processing data';
  }, [loading, month, monthFrom, monthTo, year, dateFrom, dateTo]);

  // Fetch data when filters change
  useEffect(() => {
    fetchDashboardData();
  }, [month, monthFrom, monthTo, year]);

  // Save processed dashboard to localStorage when relevant state updates
  useEffect(() => {
    if (!loading && members.length > 0) {
      const filterKey = getFilterKey();
      const cacheKey = `${DASHBOARD_CACHE_KEY}_${filterKey}`;

      const dashboardCacheData = {
        monthlyData,
        ageDistributionData,
        totalMembersCount,
        topBooksData,
        topAttendeesData,
        booksByCategoryData,
        categoryActivityData,
        dailyData,
        prevStats,
        stats,
        members,
        books,
        attendance,
        categories
      };

      try { saveToCache(cacheKey, dashboardCacheData, filterKey); } catch (e) { console.warn('Failed to save dashboard cache', e); }
    }
  }, [loading, monthlyData, ageDistributionData, stats, members, books, attendance, categories]);

  // Listen for cache invalidation events from other tabs or services
  useEffect(() => {
    const handleCacheInvalidation = () => {
      // Re-fetch dashboard data when caches are invalidated
      fetchDashboardData();
    };

    window.addEventListener('dashboardCacheInvalidated', handleCacheInvalidation);
    return () => window.removeEventListener('dashboardCacheInvalidated', handleCacheInvalidation);
  }, []);

  const chartColors = {
    attendance: '#3b82f6',
    uniqueMembers: '#10b981',
    booksBorrowed: '#f59e0b'
  };

  // Guard values for UI percentage/height calculations to avoid division by zero
  const maxBookReads = (topBooksData && topBooksData.length > 0)
    ? Math.max(...topBooksData.map(b => (b?.reads || 0))) || 1
    : 1;

  const maxAttendeeVisits = (topAttendeesData && topAttendeesData.length > 0)
    ? Math.max(...topAttendeesData.map(a => (a?.visits || 0))) || 1
    : 1;

  const StatCard: React.FC<{ 
    title: string; 
    value: string | number; 
    icon: React.ReactNode; 
    change?: string;
    color: string;
  }> = ({ title, value, icon, change, color }) => (
    <div className="relative bg-white rounded-md shadow-sm p-2 border border-gray-100 text-center">
      {typeof change !== 'undefined' && (
        <div className="absolute top-2 right-2">
          <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${parseFloat(String(change)) > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {parseFloat(String(change)) > 0 ? `+${String(change)}` : String(change)}
          </span>
        </div>
      )}

      <div className="text-xs text-gray-500 mb-1">{title}</div>

      <div className="flex items-center justify-center gap-3">
        {(() => {
          const deriveBgClass = (c: string) => {
            if (!c) return 'bg-gray-100';
            const trimmed = c.trim();
            if (trimmed.startsWith('text-')) return trimmed.replace(/^text-/, 'bg-');
            if (trimmed.startsWith('bg-')) return trimmed;
            if (/^[a-z]+-\d{3}$/.test(trimmed)) return `bg-${trimmed}`;
            return 'bg-gray-100';
          };

          const bgClass = deriveBgClass(color);
          const iconClass = `${color} h-4 w-4`;
          return (
            <div className={`w-8 h-8 flex items-center justify-center rounded-full ${bgClass} bg-opacity-10`}>
              {React.isValidElement(icon) ? React.cloneElement(icon as any, { className: iconClass }) : (
                <span className={iconClass}>{icon}</span>
              )}
            </div>
          );
        })()}

        <div className="text-xl font-semibold text-gray-900">{value}</div>
      </div>
      
      {/* Cache status indicator for dashboard */}
      {title === "Total Members" && (
        <div className="mt-1 text-[10px] text-gray-400">
          {cacheStatus}
        </div>
      )}
    </div>
  );

  // Custom tooltip component for the age pie chart
  const AgeTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const percentage = totalMembersCount > 0 ? ((data.value / totalMembersCount) * 100).toFixed(1) : '0.0';
      return (
        <div className="bg-white p-3 rounded shadow border text-sm">
          <div className="font-medium text-gray-800">{`${data.name}: ${data.value}`}</div>
          <div className="text-gray-500 text-xs mt-1">{`${percentage}%`}</div>
        </div>
      );
    }
    return null;
  };

  // Custom tooltip for daily monthly attendance chart
  const DailyTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || !payload.length) return null;
    const data = payload[0].payload || payload[0];
    const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

    // If the data point includes a full ISO date (multi-day range), use it
    if (data && data.date) {
      try {
        const d = new Date(data.date);
        const monthName = monthNames[d.getMonth()] || '';
        const formatted = `${monthName} ${d.getDate()}, ${d.getFullYear()}`;
        return (
          <div className="bg-white p-3 rounded shadow border text-sm">
            <div className="font-medium text-gray-800">{formatted}</div>
            <div className="text-gray-600 text-xs mt-1">Attendance: {data.attendance}</div>
          </div>
        );
      } catch (e) {
        // fallthrough to legacy formatting
      }
    }

    // Legacy single-month behavior: label is day number
    const selectedMonth = month || (new Date().getMonth() + 1);
    const selectedYear = year || new Date().getFullYear();
    const monthName = monthNames[selectedMonth - 1] || '';
    return (
      <div className="bg-white p-3 rounded shadow border text-sm">
        <div className="font-medium text-gray-800">{`${monthName} ${label}, ${selectedYear}`}</div>
        <div className="text-gray-600 text-xs mt-1">Attendance: {data.attendance}</div>
      </div>
    );
  };

  // Print dashboard (simple handler)
  const handlePrint = () => {
    try {
      window.print();
    } catch (e) {
      console.warn('Print not available', e);
    }
  };

  // Refresh dashboard data
  const handleRefresh = () => {
    const filterKey = getFilterKey();
    const cacheKey = `${DASHBOARD_CACHE_KEY}_${filterKey}`;
    invalidateCache(cacheKey);
    
    // Also refresh individual data caches
    invalidateCache(LIBRARY_MEMBERS_CACHE_KEY);
    invalidateCache('library_members_cache');
    invalidateCache('library_books_cache');
    invalidateCache('attendance_records_cache');
    invalidateCache('library_categories_cache');
    
    fetchDashboardData();
  };

  return (
    <div className="space-y-6">
      {/* Ensure Tailwind includes these bg- classes used dynamically for stat icon backgrounds */}
      <div className="hidden">
        <span className="bg-blue-500 bg-blue-400 bg-pink-500 bg-indigo-500 bg-purple-500 bg-orange-500 bg-yellow-500 bg-red-500 bg-gray-100" />
      </div>
      
      {/* Date Filter and Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end justify-between bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end flex-1">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-700">From Date</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
       
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-700">To Date</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
                 <button
            onClick={() => fetchDashboardData()}
            className="flex items-center gap-2 text-sm px-4 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 transition-colors whitespace-nowrap"
            title="Load Date Range"
          >
            Load
          </button>

          
          {(dateFrom || dateTo) && (
            <button
              onClick={() => {
                setDateFrom('');
                setDateTo('');
              }}
              className="px-3 py-2 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
            >
              Clear Dates
            </button>
          )}
        </div>
        
        <div className="flex gap-2">
          <button
            onClick={handleRefresh}
            className="flex items-center gap-2 text-sm px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors whitespace-nowrap"
            title="Refresh Dashboard Data"
          >
            <FaCalendarAlt className="h-3 w-3 text-gray-600" />
            Refresh
          </button>
          
      
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 text-sm px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors whitespace-nowrap"
            title="Print Dashboard Report"
          >
            <FaPrint className="h-3 w-3" />
            Print Report
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center p-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-sm text-gray-600">Loading dashboard data...</p>
            <p className="mt-2 text-xs text-gray-400">{cacheStatus}</p>
          </div>
        </div>
      ) : (
        <>
          {/* Stats Row 1 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
            <StatCard
              title="Total Members"
              value={stats.totalMembers}
              icon={<FaUsers className="h-5 w-5" />}
              color="text-blue-500"
            />
            <StatCard
              title="Male Members"
              value={stats.maleMembers}
              icon={<FaMale className="h-5 w-5" />}
              color="text-blue-400"
            />
            <StatCard
              title="Female Members"
              value={stats.femaleMembers}
              icon={<FaFemale className="h-5 w-5" />}
              color="text-pink-500"
            />
           <StatCard
              title="Active Members"
              value={stats.activeMembers}
              icon={<FaUserFriends className="h-5 w-5" />}
              color="text-indigo-500"
            />
             <StatCard
              title="Total Books"
              value={stats.totalBooks}
              icon={<FaBook className="h-5 w-5" />}
              color="text-purple-500"
            />
            <StatCard
              title="Total Attendance"
              value={stats.totalAttendance.toLocaleString()}
              icon={<FaCalendarCheck className="h-5 w-5" />}
              color="text-orange-500"
            />
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Line Chart: Attendance & Unique Members */}
            <div className="lg:col-span-2 bg-white rounded-xl shadow-soft p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-900">Monthly Activity Overview</h3>
                <div className="flex items-center gap-3 text-xs">
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    <span className="text-gray-600">Attendance</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    <span className="text-gray-600">Unique Members</span>
                  </div>
                </div>
              </div>
              <div className="h-64">
                {monthlyData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={monthlyData}>
                      <defs>
                        <linearGradient id="gradAttendance" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={chartColors.attendance} stopOpacity={0.32} />
                          <stop offset="100%" stopColor={chartColors.attendance} stopOpacity={0.06} />
                        </linearGradient>
                        <linearGradient id="gradUnique" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={chartColors.uniqueMembers} stopOpacity={0.32} />
                          <stop offset="100%" stopColor={chartColors.uniqueMembers} stopOpacity={0.06} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis 
                        dataKey="month" 
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis 
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          fontSize: '12px',
                          borderRadius: '8px',
                          border: '1px solid #e5e7eb'
                        }}
                      />
                      <Legend />
                      <Area
                        type="monotone"
                        dataKey="attendance"
                        stroke={chartColors.attendance}
                        strokeWidth={2}
                        fill="url(#gradAttendance)"
                        fillOpacity={1}
                        dot={false}
                        activeDot={{ r: 6 }}
                      />
                      <Area
                        type="monotone"
                        dataKey="uniqueMembers"
                        stroke={chartColors.uniqueMembers}
                        strokeWidth={2}
                        fill="url(#gradUnique)"
                        fillOpacity={1}
                        dot={false}
                        activeDot={{ r: 6 }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-gray-400">
                    No attendance data available
                  </div>
                )}
              </div>
            </div>

            {/* Pie Chart: Age Distribution */}
            <div className="bg-white rounded-xl shadow-soft p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-900">Age Distribution</h3>
                <FaChartPie className="h-4 w-4 text-gray-400" />
              </div>
              <div className="h-64">
                {ageDistributionData.length > 0 ? (
                  <div className="flex h-full flex-col items-center justify-center">
                    <div className="w-full h-72 md:h-80 lg:h-96 relative">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={ageDistributionData}
                            cx="50%"
                            cy="50%"
                            innerRadius={50}
                            outerRadius={95}
                            paddingAngle={2}
                            dataKey="value"
                            labelLine={false}
                            onMouseEnter={(entry: any, index: number) => {
                              const payload = entry && entry.payload ? entry.payload : entry;
                              setActiveSlice({
                                name: payload?.name,
                                value: payload?.value,
                                color: payload?.color
                              });
                            }}
                            onMouseLeave={() => setActiveSlice(null)}
                          >
                            {ageDistributionData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>

                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="text-center">
                          <div className="text-sm font-semibold text-gray-900">
                            {activeSlice ? activeSlice.name : 'Members'}
                          </div>
                          <div className="text-xs text-gray-500">
                            {activeSlice && totalMembersCount > 0
                              ? `${((activeSlice.value / totalMembersCount) * 100).toFixed(1)}% (${activeSlice.value})`
                              : `${totalMembersCount} total`}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Centered legend below the chart in 2 columns */}
                    <div className="mt-4 w-full flex items-center justify-center">
                      <div className="w-auto max-w-sm bg-white/0">
                        <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                          <div className="space-y-2">
                            {[ageDistributionData[0], ageDistributionData[1]].map((entry, i) => {
                              if (!entry) return null;
                              const pct = totalMembersCount > 0 ? ((entry.value / totalMembersCount) * 100).toFixed(1) : '0.0';
                              return (
                                <div key={`left-${i}`} className="flex items-center gap-6">
                                  <div className="flex items-center gap-2">
                                    <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: entry.color }} />
                                    <div className="text-[12px] text-gray-700">{entry.name}</div>
                                  </div>
                                  <div className="text-[12px] font-semibold text-gray-900">{pct}%</div>
                                </div>
                              );
                            })}
                          </div>

                          <div className="space-y-2">
                            {[ageDistributionData[2], ageDistributionData[3]].map((entry, i) => {
                              if (!entry) return null;
                              const pct = totalMembersCount > 0 ? ((entry.value / totalMembersCount) * 100).toFixed(1) : '0.0';
                              return (
                                <div key={`right-${i}`} className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: entry.color }} />
                                    <div className="text-[12px] text-gray-700">{entry.name}</div>
                                  </div>
                                  <div className="text-[12px] font-semibold text-gray-900">{pct}%</div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center text-gray-400">
                    No member data available
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Additional Stats Row (two cards) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Monthly Daily Attendance (1–31) */}
            <div className="bg-white rounded-xl shadow-soft p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Monthly Attendance (Days 1–31)</h3>
              <div className="h-56">
                {dailyData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dailyData} margin={{ top: 8, right: 8, left: -12, bottom: 26 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis
                        dataKey="day"
                        fontSize={10}
                        tickLine={false}
                        axisLine={false}
                        label={{ value: 'Daily Attendance', position: 'bottom', offset: -10, style: { fontSize: 12, fill: '#10b981' } }}
                      />
                      <YAxis fontSize={10} tickLine={false} axisLine={false} />
                      <Tooltip content={<DailyTooltip />} />
                      <Bar dataKey="attendance" name="Attendance" fill="#10b981" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-gray-400">
                    No attendance data available for this month
                  </div>
                )}
              </div>
            </div>

            {/* Member Status Overview */}
            <div className="bg-white rounded-xl shadow-soft p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Member Status Overview</h3>
              <div className="h-56">
                <div className="flex flex-col h-full space-y-3">
                  {/* Active Members */}
                  <div className="flex items-center p-2.5 bg-gradient-to-r from-blue-50 to-blue-25 rounded-lg border border-blue-100">
                    <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-blue-100 mr-3">
                      <FaUserCheck className="h-4 w-4 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-baseline">
                        <span className="text-xs font-medium text-gray-700">Active</span>
                        <span className="text-base font-bold text-gray-900">{stats.activeMembers}</span>
                      </div>
                      <div className="flex items-center justify-between mt-0.5">
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center text-xs">
                            <FaMale className="h-3 w-3 text-blue-400 mr-1" />
                            <span className="text-gray-600">{stats.activeMale}</span>
                          </div>
                          <div className="flex items-center text-xs">
                            <FaFemale className="h-3 w-3 text-pink-500 mr-1" />
                            <span className="text-gray-600">{stats.activeFemale}</span>
                          </div>
                        </div>
                        {(stats.activeMembers - prevStats.activeMembers) !== 0 && (
                          <span className={`text-xs px-1.5 py-0.5 rounded-full ${(stats.activeMembers - prevStats.activeMembers) > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {(stats.activeMembers - prevStats.activeMembers) > 0 ? '↑' : '↓'} {Math.abs(stats.activeMembers - prevStats.activeMembers)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Semi-active Members */}
                  <div className="flex items-center p-2.5 bg-gradient-to-r from-amber-50 to-amber-25 rounded-lg border border-amber-100">
                    <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-amber-100 mr-3">
                      <FaUserCheck className="h-4 w-4 text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-baseline">
                        <span className="text-xs font-medium text-gray-700">Semi-active</span>
                        <span className="text-base font-bold text-gray-900">{stats.semiActiveMembers}</span>
                      </div>
                      <div className="flex items-center justify-between mt-0.5">
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center text-xs">
                            <FaMale className="h-3 w-3 text-blue-400 mr-1" />
                            <span className="text-gray-600">{stats.semiActiveMale}</span>
                          </div>
                          <div className="flex items-center text-xs">
                            <FaFemale className="h-3 w-3 text-pink-500 mr-1" />
                            <span className="text-gray-600">{stats.semiActiveFemale}</span>
                          </div>
                        </div>
                        {(stats.semiActiveMembers - prevStats.semiActiveMembers) !== 0 && (
                          <span className={`text-xs px-1.5 py-0.5 rounded-full ${(stats.semiActiveMembers - prevStats.semiActiveMembers) > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {(stats.semiActiveMembers - prevStats.semiActiveMembers) > 0 ? '↑' : '↓'} {Math.abs(stats.semiActiveMembers - prevStats.semiActiveMembers)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Inactive Members */}
                  <div className="flex items-center p-2.5 bg-gradient-to-r from-red-50 to-red-25 rounded-lg border border-red-100">
                    <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-red-100 mr-3">
                      <FaUserTimes className="h-4 w-4 text-red-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-baseline">
                        <span className="text-xs font-medium text-gray-700">Inactive</span>
                        <span className="text-base font-bold text-gray-900">{stats.inactiveMembers}</span>
                      </div>
                      <div className="flex items-center justify-between mt-0.5">
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center text-xs">
                            <FaMale className="h-3 w-3 text-blue-400 mr-1" />
                            <span className="text-gray-600">{stats.inactiveMale}</span>
                          </div>
                          <div className="flex items-center text-xs">
                            <FaFemale className="h-3 w-3 text-pink-500 mr-1" />
                            <span className="text-gray-600">{stats.inactiveFemale}</span>
                          </div>
                        </div>
                        {(stats.inactiveMembers - prevStats.inactiveMembers) !== 0 && (
                          <span className={`text-xs px-1.5 py-0.5 rounded-full ${(stats.inactiveMembers - prevStats.inactiveMembers) > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {(stats.inactiveMembers - prevStats.inactiveMembers) > 0 ? '↑' : '↓'} {Math.abs(stats.inactiveMembers - prevStats.inactiveMembers)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* Library Activity by Category (full-width row) */}
          <div className="bg-white rounded-xl shadow-soft p-4 mt-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Library Activity by Category</h3>
            <div className="h-72">
              {categoryActivityData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={categoryActivityData} margin={{ top: 8, right: 8, left: -12, bottom: 26 }}>
                    <defs>
                      <linearGradient id="gradCategory" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={chartColors.attendance} stopOpacity={0.32} />
                        <stop offset="100%" stopColor={chartColors.attendance} stopOpacity={0.06} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="category"
                      fontSize={10}
                      tickLine={false}
                      axisLine={false}
                      interval={0}
                      height={64}
                      angle={-45}
                      textAnchor="end"
                    />
                    <YAxis fontSize={10} tickLine={false} axisLine={false} />
                    <Tooltip />
                    <Area
                      type="monotone"
                      dataKey="reads"
                      stroke={chartColors.attendance}
                      strokeWidth={2}
                      fill="url(#gradCategory)"
                      fillOpacity={1}
                      dot={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-gray-400">
                  No category activity data available
                </div>
              )}
            </div>
          </div>

          {/* Tables Row - Compact Version */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Top 10 Most Popular Books - Compact */}
            <div className="bg-white rounded-xl shadow-soft overflow-hidden">
              <div className="px-3 py-2 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-blue-25">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FaStar className="h-3 w-3 text-yellow-500" />
                    <h3 className="text-xs font-semibold text-gray-900">Top 10 Popular Books</h3>
                  </div>
                  <span className="text-[10px] bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded-full">
                    {topBooksData.length} books
                  </span>
                </div>
              </div>
              <div className="overflow-x-auto">
                {topBooksData.length > 0 ? (
                  <table className="min-w-full divide-y divide-gray-200 text-xs">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-2 py-1 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">#</th>
                        <th className="px-2 py-1 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider w-3/5">Book Title</th>
                        <th className="px-2 py-1 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Reads</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {topBooksData.map((book, index) => (
                        <tr key={book.id} className="hover:bg-gray-50 transition-colors duration-150">
                          <td className="px-2 py-1.5 whitespace-nowrap">
                            <div className={`w-4 h-4 flex items-center justify-center rounded-full text-[9px] font-medium ${
                              index === 0 ? 'bg-yellow-100 text-yellow-800' :
                              index === 1 ? 'bg-gray-100 text-gray-800' :
                              index === 2 ? 'bg-amber-100 text-amber-800' :
                              'bg-gray-50 text-gray-500'
                            }`}>
                              {index + 1}
                            </div>
                          </td>
                          <td className="px-2 py-1.5 w-3/5">
                            <div className="flex flex-col">
                              <div className="text-[11px] font-medium text-gray-900 whitespace-normal break-words">
                                {book.title}
                              </div>
                              <div className="text-[10px] text-gray-500">
                                {book.author || 'Unknown author'}
                              </div>
                            </div>
                          </td>
                          <td className="px-2 py-1.5 whitespace-nowrap">
                            <div className="flex items-center gap-1.5">
                              <div className="w-12 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-blue-500 rounded-full"
                                  style={{ 
                                    width: `${Math.min(( (book.reads || 0) / maxBookReads) * 100, 100)}%` 
                                  }}
                                ></div>
                              </div>
                              <div className="text-[11px] font-semibold text-gray-900 min-w-[30px]">
                                {book.reads}
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="p-4 text-center text-gray-400 text-xs">
                    No book data available
                  </div>
                )}
              </div>
            </div>

            {/* Top 10 Attendees - Compact */}
            <div className="bg-white rounded-xl shadow-soft overflow-hidden">
              <div className="px-3 py-2 border-b border-gray-100 bg-gradient-to-r from-green-50 to-green-25">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FaList className="h-3 w-3 text-green-600" />
                    <h3 className="text-xs font-semibold text-gray-900">Top 10 Attendees</h3>
                  </div>
                  <span className="text-[10px] bg-green-100 text-green-800 px-1.5 py-0.5 rounded-full">
                    {topAttendeesData.length} members
                  </span>
                </div>
              </div>
              <div className="overflow-x-auto">
                {topAttendeesData.length > 0 ? (
                  <table className="min-w-full divide-y divide-gray-200 text-xs">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-2 py-1 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">#</th>
                        <th className="px-2 py-1 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Member</th>
                        <th className="px-2 py-1 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Visits</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {topAttendeesData.map((attendee, index) => (
                        <tr key={attendee.id} className="hover:bg-gray-50 transition-colors duration-150">
                          <td className="px-2 py-1.5 whitespace-nowrap">
                            <div className={`w-4 h-4 flex items-center justify-center rounded-full text-[9px] font-medium ${
                              index === 0 ? 'bg-green-100 text-green-800' :
                              index === 1 ? 'bg-gray-100 text-gray-800' :
                              index === 2 ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-50 text-gray-500'
                            }`}>
                              {index + 1}
                            </div>
                          </td>
                          <td className="px-2 py-1.5">
                            <div className="flex flex-col">
                              <div className="text-[11px] font-medium text-gray-900 truncate max-w-[150px]">
                                {attendee.name}
                              </div>
                              <div className="text-[10px] text-gray-500 truncate max-w-[150px]">
                                ID: {attendee.memberId.slice(0, 8)}...
                              </div>
                            </div>
                          </td>
                          <td className="px-2 py-1.5 whitespace-nowrap">
                            <div className="flex items-center gap-1.5">
                              <div className="flex-1">
                                <div className="text-[11px] font-semibold text-gray-900">
                                  {attendee.visits}
                                </div>
                                <div className="text-[9px] text-gray-500">
                                  {Math.round(((attendee.visits || 0) / maxAttendeeVisits) * 100)}% of top
                                </div>
                              </div>
                              <div className="w-2 h-8 bg-gray-200 rounded-full overflow-hidden relative">
                                <div 
                                  className="absolute bottom-0 left-0 right-0 bg-green-500 rounded-full"
                                  style={{ 
                                    height: `${Math.min(((attendee.visits || 0) / maxAttendeeVisits) * 100, 100)}%` 
                                  }}
                                ></div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="p-4 text-center text-gray-400 text-xs">
                    No attendance data available
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default LibraryDashboard;
